import { ReactElement, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import SearchInput from '../controls/SearchInput';
import Filter from '../controls/Filter';
import UsersTable from './UsersTable';
import AddUserModal from '../modals/AddUser';
import { useData } from '../context/data.context';
import Loader from './loader';
import { ActionData, UserDetails } from '../constants/telosair-interfaces';
import { deleteUser, resendTempPassword } from '../services/dashboard-service';
import { TOAST_MESSAGES } from '../constants/global-constants';

/**
 * Users component renders the list of users to add or edit.
 * @returns - Users Tab Component.
 */
export default function UsersTab(): ReactElement {
  const { clientUsers, fetchAllUserList, loading } = useData();
  const [searchText, setSearchText] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [actionData, setActionData] = useState<ActionData<UserDetails>>(
    {} as ActionData<UserDetails>
  );

  /**
   * Handles delete user.
   * @param id - User id.
   */
  const handleDeleteUser = async (id: number): Promise<void> => {
    try {
      await deleteUser(id);
      toast.success(TOAST_MESSAGES.deleted);
      await fetchAllUserList();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(e);
      // A silent failure here looked like the button doing nothing; show the
      // server's reason so a stuck user can actually be reported.
      toast.error(e?.response?.data?.message || TOAST_MESSAGES.error);
    }
  };

  /**
   * Function to operate delete and edit group.
   * @param action - Action data needs type and group data.
   */
  const handleAction = async (action: ActionData<UserDetails>): Promise<void> => {
    try {
      if (action.type === 'delete') {
        await handleDeleteUser(action.data.id);
      } else if (action.type === 'edit') {
        setActionData(action);
        setAddModalOpen(true);
      } else if (action.type === 'resend-invite') {
        await resendTempPassword(action.data.userName);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  const users: (UserDetails & { action: (action: ActionData<UserDetails>) => void })[] =
    clientUsers.map((item) => ({
      ...item,
      action: handleAction
    }));

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-4 justify-between rounded bg-white dark:bg-container-500 p-4 mt-4 items-center">
        <div className="flex items-center space-x-4 invisible">
          <SearchInput textValue={searchText} setText={setSearchText} />
          <Filter position="left" />
        </div>
        <div className="mr-4">
          <Button
            theme="primary"
            size="sm"
            name="Add New Users"
            onClick={() => setAddModalOpen(true)}
            isBlockButton
          />
        </div>
      </div>
      <div className="mt-4">
        {/* {main content} */}
        {loading && <Loader />}
        {!loading && <UsersTable users={users} />}
      </div>
      {addModalOpen && (
        <AddUserModal
          handleClose={() => {
            setAddModalOpen(false);
            fetchAllUserList();
          }}
          actionData={actionData}
        />
      )}
    </>
  );
}
